import React from "react";
import { Female, Male } from "@mui/icons-material";
import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { themePalette } from "../../theme";
import { ClockIcon } from "../../assets/iconComponents/ClockIcon";
import { NullifyIcon } from "../../assets/iconComponents/NullifyIcon";
import { ReportIcon } from "../../assets/iconComponents/EyeIcon";
import { NegativeIcon } from "../../assets/iconComponents/Negative";
import { PlusCircleFill } from "../../assets/iconComponents/PlusCircleFill";
import {
  GridReportIconSlot,
  ReportStatusContent,
  ReportStatusPrimary,
  ReportStatusSecondary,
  ReportStatusStack,
} from "./Grid.styles";

export interface IconResult {
  icon: React.ReactNode;
  styles: React.CSSProperties;
}

export const getIcon = (value: string, isMuted = false): IconResult => {
  const lowerCaseValue = value.toLowerCase();
  if (lowerCaseValue === "see report") {
    return {
      icon: <ReportIcon />,
      styles: { color: themePalette.greyMedium },
    };
  }
  if (["negative", "low risk", "not detected"].includes(lowerCaseValue)) {
    return {
      icon: isMuted ? (
        <FontAwesomeIcon icon={faCircleCheck} />
      ) : (
        <NegativeIcon />
      ),
      styles: { color: themePalette.greyMedium },
    };
  }
  if (lowerCaseValue === "likely positive") {
    return { icon: <PlusCircleFill />, styles: {} };
  }
  if (lowerCaseValue === "detected") {
    return {
      icon: null,
      styles: { color: themePalette.greyMedium },
    };
  }
  if (lowerCaseValue === "processing") {
    return { icon: <ClockIcon />, styles: { color: themePalette.greyMedium } };
  }
  if (lowerCaseValue === "opted out" || lowerCaseValue === "not ordered") {
    return {
      icon: <NullifyIcon />,
      styles: {
        color: themePalette.greyMedium,
        fontFamily: "light",
        fontWeight: 400,
      },
    };
  }
  if (lowerCaseValue === "download report") {
    return {
      icon: null,
      styles: { textDecoration: "underline", color: themePalette.greenDark },
    };
  }
  if (lowerCaseValue === "m") {
    return { icon: <Male />, styles: {} };
  }
  if (lowerCaseValue === "f") {
    return { icon: <Female />, styles: {} };
  }
  if (lowerCaseValue === "mm") {
    return {
      icon: (
        <>
          <Male />
          <Male />
        </>
      ),
      styles: {},
    };
  }
  if (lowerCaseValue === "ff") {
    return {
      icon: (
        <>
          <Female />
          <Female />
        </>
      ),
      styles: {},
    };
  }
  if (lowerCaseValue === "mf") {
    return {
      icon: (
        <>
          <Male />
          <Female />
        </>
      ),
      styles: {},
    };
  }
  return { icon: null, styles: {} };
};

interface ReportStatusIconProps {
  value: string;
  icon?: React.ReactNode;
  secondaryLabel?: React.ReactNode;
  isMuted?: boolean;
}

export const ReportStatusIcon: React.FC<ReportStatusIconProps> = ({
  value,
  icon,
  secondaryLabel,
  isMuted = false,
}) => {
  const { icon: defaultIcon, styles } = getIcon(value, isMuted);
  const statusIcon = icon === undefined ? defaultIcon : icon;
  const valueStyles = isMuted
    ? { ...styles, color: themePalette.neutral500 }
    : styles;

  return (
    <ReportStatusContent $hasSecondaryLabel={Boolean(secondaryLabel)}>
      {statusIcon != null && (
        <GridReportIconSlot $isMuted={isMuted}>{statusIcon}</GridReportIconSlot>
      )}
      {secondaryLabel ? (
        <ReportStatusStack>
          <ReportStatusPrimary style={valueStyles}>{value}</ReportStatusPrimary>
          <ReportStatusSecondary $isMuted={isMuted}>
            {secondaryLabel}
          </ReportStatusSecondary>
        </ReportStatusStack>
      ) : (
        <span style={valueStyles}>{value}</span>
      )}
    </ReportStatusContent>
  );
};
